import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Clock, Receipt, ClipboardList, Calendar, CalendarRange, MapPin, ChevronRight,
  FolderKanban, FileCheck2, AlertTriangle, Truck, Package,
  ShieldCheck, Users, FileText, Wrench, UserCircle, Boxes, Zap, RadioTower, FolderOpen, BookOpen, FlaskConical, Siren } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { useMyProjectIds } from "@/lib/useMyProjectIds";
import { usePoliciesDue } from "@/lib/usePoliciesDue";
import { useAlertsDue } from "@/lib/useAlertsDue";
import { Card } from "@/components/ui";
import { useJmpLive, JmpLiveCard } from "@/features/jmp/jmpLive";   // mig 445 — the live journey, one tap to check in
import { format, parseISO, isToday, isTomorrow } from "date-fns";

// =============================================================================
// FieldMobileHome — slim mobile home for field / subcontractor (T-074).
//
// Per James 2026-04-25:
//   "really clean looking screens for the phone with field users getting a
//    super slim down version thats easy to navigate and just the important
//    to them functions"
//
// Three things matter for a rigger on site:
//   1. Where am I working today? — "Today's site" hero card
//   2. What's coming up? — bookings on their projects this week
//   3. The 3 things I do every day — Log hours / Log receipt / HOP photo
//
// Everything else is one tap away in the bottom nav. No KPI tiles, no
// company-wide widgets, no clutter.
// =============================================================================

export default function FieldMobileHome() {
  const user = useRequireAuth();
  // T-245 — single source of truth for "my projects": team direct + crew membership.
  // Both Today's-site and This-week queries scope to this set so they line up
  // with FieldProjectsListScreen and MyWeekScreen.
  const { projectIds: myProjectIds } = useMyProjectIds(user);
  // mig 445 (22 Oct 2026) — my journey underway sits at the very top. James,
  // road week: "the existing check-in is nested / hidden away in HSE and hard
  // for the guys to find."
  const { data: jmpLive = [] } = useJmpLive(!!user?.employee_id);
  const myJourneys = jmpLive.filter(j => j.traveller_employee_id === user?.employee_id);

  // -- Today's site — most recently updated active project among my projects.
  // 2026-05-09 — was including acceptance_pending (project over) and projects
  // whose scheduled_start_date hasn't arrived yet (mobilising future-start).
  // Now strict: live + mobilising-with-start-date-passed, and not finished.
  const { data: todaysProject } = useQuery({
    queryKey: ["field_todays_project", user?.employee_id, myProjectIds.join(",")],
    enabled: !!user?.employee_id,
    queryFn: async () => {
      if (myProjectIds.length === 0) return null;
      const today = new Date().toISOString().slice(0, 10);
      const { data: projects } = await supabase
        .from("projects")
        .select(`project_id, project_code, project_name, status, updated_at,
                 scheduled_start_date, actual_end_date,
                 site:site_id(site_name, site_code, suburb, state, address_line1)`)
        .in("project_id", myProjectIds)
        .in("status", ["mobilising", "live"])              // exclude acceptance_pending / closed
        .is("actual_end_date", null)                        // not finished
        .or(`scheduled_start_date.is.null,scheduled_start_date.lte.${today}`)  // already started or open-start
        .order("updated_at", { ascending: false })
        .limit(1);
      return projects?.[0] ?? null;
    },
  });

  // -- This week's bookings on their projects.
  // T-245 — was relying on RLS alone (HANDOVER Task #38 leak: occasional rows
  // appeared from projects the user isn't on). Now scoped client-side by
  // myProjectIds in addition to RLS so the list always matches "my projects".
  const { data: weekBookings = [] } = useQuery({
    queryKey: ["field_week_bookings", user?.employee_id, myProjectIds.join(",")],
    enabled: !!user?.employee_id,
    queryFn: async () => {
      if (myProjectIds.length === 0) return [];
      const weekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from("v_upcoming_bookings")
        .select("*")
        .in("project_id", myProjectIds)
        .lte("scheduled_start", weekFromNow)
        .order("scheduled_start")
        .limit(4);
      return data ?? [];
    },
  });

  // -- Licence warnings
  const { data: licenceWarnings } = useQuery({
    queryKey: ["field_licence_warnings", user?.employee_id],
    enabled: !!user?.employee_id,
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const in60  = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const [overdueRes, soonRes] = await Promise.all([
        supabase.from("employee_licences")
          .select("licence_id", { count: "exact", head: true })
          .eq("employee_id", user!.employee_id!)
          .eq("is_active", true)
          .lt("expiry_date", today),
        supabase.from("employee_licences")
          .select("licence_id", { count: "exact", head: true })
          .eq("employee_id", user!.employee_id!)
          .eq("is_active", true)
          .gte("expiry_date", today)
          .lte("expiry_date", in60),
      ]);
      return {
        overdue: overdueRes.count ?? 0,
        soon:    soonRes.count ?? 0,
      };
    },
  });

  // 29 Sep 2026 — James, showing the guys in the field: "the crew can't find
  // the policies". A nudge on the home screen when there is something to
  // read and sign, and a tile so the screen is always one tap away.
  const policiesDue = usePoliciesDue(user?.user_id);
  const alertsDue = useAlertsDue(user?.employee_id);   // mig 413 — HSE alerts to acknowledge

  if (!user) return null;
  const firstName = user.first_name ?? "there";
  const greeting = ((): string => {
    const h = new Date().getHours();
    if (h < 12) return "Morning";
    if (h < 17) return "Afternoon";
    return "Evening";
  })();

  return (
    <div className="p-4 pb-20 max-w-[640px] mx-auto">
      {/* Greeting */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-1 w-10 bg-accent rounded-full" />
          <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
            {format(new Date(), "EEE dd MMM")}
          </div>
        </div>
        <h1 className="text-[24px] font-semibold tracking-tight">
          {greeting}, {firstName}
        </h1>
      </div>

      {/* Policies to read and sign — the nudge (29 Sep 2026). Red because an
          unsigned policy is a compliance gap, and it sits above the site so
          it is the first thing seen, not the last. */}
      {/* HSE alerts to acknowledge (mig 413, 3 Oct 2026) — above the policies
          nudge: an urgent client alert is the most time-critical thing on the
          phone (24 h), so it is the first card on the home screen. */}
      {alertsDue.total > 0 && (
        <Link to="/alerts" className="block mb-3">
          <Card className={alertsDue.urgent > 0 || alertsDue.overdue > 0 ? "border-danger/60 bg-danger/10" : "border-warn/40 bg-warn/5"}>
            <div className="p-3 flex items-center gap-3">
              <Siren size={16} className={alertsDue.urgent > 0 || alertsDue.overdue > 0 ? "text-danger" : "text-warn"} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-text">
                  {alertsDue.total} HSE alert{alertsDue.total === 1 ? "" : "s"} to acknowledge
                  {alertsDue.urgent > 0 && <span className="text-danger"> · {alertsDue.urgent} urgent</span>}
                  {alertsDue.overdue > 0 && <span className="text-danger"> · {alertsDue.overdue} overdue</span>}
                </div>
                <div className="text-[11px] text-text-muted mt-0.5">
                  {alertsDue.urgent > 0 ? "Urgent alerts need a read and a tick within 24 hours" : "Read each one and tick it before the next toolbox"}
                </div>
              </div>
              <ChevronRight size={14} className="text-text-muted flex-shrink-0" />
            </div>
          </Card>
        </Link>
      )}
      {policiesDue > 0 && (
        <Link to="/policies" className="block mb-5">
          <Card className="border-danger/40 bg-danger/5">
            <div className="p-3 flex items-center gap-3">
              <FileText size={16} className="text-danger" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-text">
                  {policiesDue} polic{policiesDue === 1 ? "y" : "ies"} to read and sign
                </div>
                <div className="text-[11px] text-text-muted mt-0.5">
                  Tap to open, read and acknowledge — takes a minute each
                </div>
              </div>
              <ChevronRight size={14} className="text-text-muted flex-shrink-0" />
            </div>
          </Card>
        </Link>
      )}

      {/* mig 445 — journey underway */}
      {myJourneys.map(j => <div key={j.jmp_id} className="mb-5"><JmpLiveCard j={j} mine returnTo="/"/></div>)}

      {/* Today's site hero */}
      {todaysProject ? (
        <Link to={`/projects/${todaysProject.project_id}`} className="block mb-5 group">
          <Card className="border-accent/30 bg-gradient-to-br from-accent/8 to-transparent">
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-2 inline-flex items-center gap-1.5">
                <MapPin size={10} className="text-accent"/>
                Today's site
              </div>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-mono text-text-muted">
                    {todaysProject.project_code}
                  </div>
                  <div className="text-[18px] font-semibold text-text mt-0.5 line-clamp-2">
                    {todaysProject.project_name}
                  </div>
                  {(todaysProject as any).site && (
                    <div className="text-[12px] text-text-muted mt-1.5 space-y-0.5">
                      <div>{(todaysProject as any).site.site_name}</div>
                      {(todaysProject as any).site.suburb && (
                        <div className="text-[11px] text-text-dim">
                          {[(todaysProject as any).site.suburb, (todaysProject as any).site.state].filter(Boolean).join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <ChevronRight size={18} className="text-accent flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </Card>
        </Link>
      ) : (
        <Card className="mb-5">
          <div className="p-5 text-center">
            <FolderKanban size={20} className="text-text-dim mx-auto mb-2"/>
            <div className="text-[13px] text-text-muted">
              No active projects assigned to you yet.
            </div>
            <div className="text-[11px] text-text-dim mt-1">
              Check with your supervisor.
            </div>
          </div>
        </Card>
      )}

      {/* Big-touch quick actions — 2-col grid for thumb-reach */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        {/* mig 392 — today's site folder: hospital, who to call, map, report */}
        {todaysProject && (
          <BigAction
            to={`/projects/${todaysProject.project_id}/site-folder`}
            label="Site folder"
            icon={FolderOpen}
            colour="text-accent"
          />
        )}
        <BigAction
          to="/my-schedule"
          label="My schedule"
          icon={CalendarRange}
          colour="text-accent"
        />
        <BigAction
          to="/timesheets"
          label="Log hours"
          icon={Clock}
          colour="text-accent"
        />
        <BigAction
          to="/receipts/new"
          label="Log receipt"
          icon={Receipt}
          colour="text-accent"
        />
        <BigAction
          to="/acceptance"
          label="HOPs"
          icon={ClipboardList}
          colour="text-accent"
        />
        <BigAction
          to="/inspections"
          label="Inspections"
          icon={ShieldCheck}
          colour="text-accent"
        />
        <BigAction
          to="/mops"
          label="MOPs library"
          icon={BookOpen}
          colour="text-accent"
        />
        <BigAction
          to="/policies"
          label={policiesDue > 0 ? `Policies · ${policiesDue} to sign` : "Policies"}
          icon={FileText}
          colour={policiesDue > 0 ? "text-danger" : "text-accent"}
        />
        <BigAction
          to="/alerts"
          label={alertsDue.total > 0 ? `HSE alerts · ${alertsDue.total} to sign` : "HSE alerts"}
          icon={Siren}
          colour={alertsDue.total > 0 ? "text-danger" : "text-accent"}
        />
        {/* SDS register (mig 407, 30 Sep 2026) — read a sheet, or add a product with the camera */}
        <BigAction
          to="/sds"
          label="SDS register"
          icon={FlaskConical}
          colour="text-accent"
        />
      </div>

      {/* This week's bookings */}
      {weekBookings.length > 0 && (
        <div className="mb-5">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-1 w-8 bg-accent rounded-full" />
              <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
                This week
              </div>
            </div>
            {/* T-245 — direct entry into the field-friendly weekly view */}
            <Link to="/my-schedule" className="text-[11px] font-mono text-accent hover:underline">
              My schedule →
            </Link>
          </div>
          <div className="space-y-2">
            {weekBookings.map((b: any) => <BookingMiniRow key={b.booking_id} booking={b} />)}
          </div>
        </div>
      )}

      {/* Licence warnings (if any) */}
      {licenceWarnings && (licenceWarnings.overdue > 0 || licenceWarnings.soon > 0) && (
        <Link to="/licences" className="block mb-5">
          <Card className={`border-${licenceWarnings.overdue > 0 ? "danger" : "warn"}/30 bg-${licenceWarnings.overdue > 0 ? "danger" : "warn"}/5`}>
            <div className="p-3 flex items-center gap-3">
              <AlertTriangle size={16} className={licenceWarnings.overdue > 0 ? "text-danger" : "text-warn"} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-text">
                  {licenceWarnings.overdue > 0
                    ? `${licenceWarnings.overdue} licence${licenceWarnings.overdue === 1 ? "" : "s"} overdue`
                    : `${licenceWarnings.soon} licence${licenceWarnings.soon === 1 ? "" : "s"} expiring soon`}
                </div>
                <div className="text-[11px] text-text-muted mt-0.5">
                  Tap to review and re-upload
                </div>
              </div>
              <ChevronRight size={14} className="text-text-muted flex-shrink-0" />
            </div>
          </Card>
        </Link>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function BigAction({
  to, label, icon: Icon, colour,
}: {
  to: string; label: string; icon: any; colour: string;
}) {
  return (
    <Link to={to} className="block group">
      <Card className="h-full hover:border-accent transition-colors">
        <div className="p-5 flex flex-col items-center justify-center text-center min-h-[100px]">
          <Icon size={28} className={`${colour} mb-2`} />
          <div className="text-[14px] font-semibold text-text">{label}</div>
        </div>
      </Card>
    </Link>
  );
}

// -----------------------------------------------------------------------------
function BookingMiniRow({ booking }: { booking: any }) {
  const start = parseISO(booking.scheduled_start);
  const dayLabel = isToday(start) ? "Today"
                 : isTomorrow(start) ? "Tomorrow"
                 : format(start, "EEE dd MMM");

  const TYPE_ICON: Record<string, any> = {
    plant: Truck, materials: Boxes, delivery: Package, electrical: Zap,
    integration: ShieldCheck, subcontractor: Users, meeting: Calendar,
    inspection: FileText, outage: RadioTower, other: Wrench,
  };
  const Icon = TYPE_ICON[booking.booking_type] ?? Wrench;

  return (
    // T-245 → 3 Sep: My week folded into My schedule — bookings land there.
    <Link to="/my-schedule" className="block">
      <Card>
        <div className="p-3 flex items-start gap-3">
          <Icon size={14} className="text-accent flex-shrink-0 mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-text font-medium truncate">{booking.title}</div>
            <div className="text-[11px] font-mono text-text-muted mt-0.5">
              {dayLabel} · {format(start, "HH:mm")}
            </div>
            {booking.project_code && (
              <div className="text-[10px] text-text-dim mt-0.5 truncate">
                {booking.project_code}
              </div>
            )}
          </div>
        </div>
      </Card>
    </Link>
  );
}
